import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, RefreshCw } from "lucide-react";

import { useAppAuth } from "@/components/auth/AuthProvider";
import {
  InlineMessage,
  MonitorButton,
  MonitorPage,
  PageHeading,
  SettingRow,
} from "@/components/monitor/MonitorUI";
import { useDeviceType } from "@/hooks";
import type { DeviceType } from "@/types";
import styles from "./SettingsPage.module.scss";

type PendingAction = "switch" | "signOut" | "signOutAll" | null;

export function SettingsPage() {
  const navigate = useNavigate();
  const { signOut, signOutAll, user } = useAppAuth();
  const {
    deviceType,
    setDeviceType,
    isLoading,
    error: deviceError,
  } = useDeviceType();
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [error, setError] = useState<string | null>(null);
  const pendingRef = useRef(false);

  const runAction = async (
    action: Exclude<PendingAction, null>,
    task: () => Promise<boolean | void>,
  ) => {
    if (pendingRef.current) return false;

    pendingRef.current = true;
    setPendingAction(action);
    setError(null);

    try {
      const result = await task();
      if (result === false) {
        setError("We couldn't complete that action. Please try again.");
        return false;
      }
      return true;
    } catch (err) {
      console.error(`Settings ${action} error:`, err);
      setError("We couldn't complete that action. Please try again.");
      return false;
    } finally {
      pendingRef.current = false;
      setPendingAction(null);
    }
  };

  const handleChangeDeviceType = async () => {
    if (!deviceType) return;
    const nextType: DeviceType = deviceType === "BABY" ? "PARENT" : "BABY";
    const success = await runAction("switch", () => setDeviceType(nextType));
    if (success) navigate("/app");
  };

  const handleLogout = async () => {
    const success = await runAction("signOut", async () => {
      await signOut();
    });
    if (success) navigate("/");
  };

  const handleLogoutAll = async () => {
    const success = await runAction("signOutAll", async () => {
      await signOutAll();
    });
    if (success) navigate("/");
  };

  const isBaby = deviceType === "BABY";
  const email = user?.primaryEmailAddress?.emailAddress;

  return (
    <MonitorPage className={styles.page}>
      <PageHeading title="Settings" />

      {error || deviceError ? (
        <InlineMessage>{error || deviceError}</InlineMessage>
      ) : null}

      <section className={styles.group} aria-labelledby="this-device-heading">
        <h2 id="this-device-heading" className={styles.groupTitle}>
          This device
        </h2>
        <SettingRow
          title="Current mode"
          description={
            !deviceType
              ? "Choose a mode in the monitor to get started."
              : isBaby
                ? "Place this device facing your baby and start streaming when you are ready."
                : "Watch and listen to a baby device signed in to the same account."
          }
        >
          <span className={styles.currentValue}>
            {deviceType
              ? isBaby
                ? "Baby Mode"
                : "Parent Mode"
              : isLoading
                ? "Loading…"
                : "Not set"}
          </span>
        </SettingRow>
        <SettingRow
          title={
            deviceType
              ? `Switch to ${isBaby ? "Parent" : "Baby"} Mode`
              : "Choose a mode"
          }
          description="You can change this role at any time."
        >
          {!deviceType ? (
            <Link to="/app" className={styles.helpLink}>
              Choose mode
            </Link>
          ) : (
            <MonitorButton
              variant="secondary"
              onClick={() => void handleChangeDeviceType()}
              loading={pendingAction === "switch"}
              loadingLabel="Switching…"
              disabled={
                isLoading ||
                Boolean(pendingAction && pendingAction !== "switch")
              }
            >
              <RefreshCw size={17} aria-hidden="true" />
              <span>Switch</span>
            </MonitorButton>
          )}
        </SettingRow>
      </section>

      <section className={styles.group} aria-labelledby="account-heading">
        <h2 id="account-heading" className={styles.groupTitle}>
          Account
        </h2>
        <SettingRow
          title="Signed-in email"
          description={email ?? "No email is available for this account."}
          className={styles.emailRow}
        />
        <SettingRow title="Sign out" description="Sign out on this device.">
          <MonitorButton
            variant="secondary"
            onClick={() => void handleLogout()}
            loading={pendingAction === "signOut"}
            loadingLabel="Signing out…"
            disabled={Boolean(pendingAction && pendingAction !== "signOut")}
          >
            <LogOut size={17} aria-hidden="true" />
            <span>Sign out</span>
          </MonitorButton>
        </SettingRow>
        <SettingRow
          title="Sign out all devices"
          description="End sessions on every device."
        >
          <MonitorButton
            variant="destructive"
            onClick={() => void handleLogoutAll()}
            loading={pendingAction === "signOutAll"}
            loadingLabel="Signing out all devices…"
            disabled={Boolean(pendingAction && pendingAction !== "signOutAll")}
          >
            <LogOut size={17} aria-hidden="true" />
            <span>Sign out all</span>
          </MonitorButton>
        </SettingRow>
      </section>

      <section className={styles.group} aria-labelledby="help-heading">
        <h2 id="help-heading" className={styles.groupTitle}>
          Help
        </h2>
        <SettingRow
          title="Support"
          description="Tell us what went wrong or what would make GoodBaby better."
        >
          <Link to="/support" className={styles.helpLink}>
            Open Support
          </Link>
        </SettingRow>
      </section>
    </MonitorPage>
  );
}
